import { Box } from '@mui/material';
import type { AlertColor } from '@mui/material';
import Login from './Login';
import { useState, useRef, useEffect } from 'react';
import CustomSnackbar from '@ui-components/Snackbar/CustomSnackbar';
import QLerLogo from '@assets/images/qler_Logo.svg';
import './signin.css';
import '@utils/utilities.css';
import { useLocation, useNavigate } from 'react-router-dom';
import QlerFooter from './Footer';
import type {
  FacilityLogin,
  FacilityLoginResponse,
  FacilityResponce,
} from '../../API/services/loginServices';
import { useFacilityLogin } from '../../API/hooks/loginHook';
import {
  CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH,
  CONSULT_WAITING_SCREEN_ROUTE_PATH,
  FACILITY_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH,
  FACILITY_FORGOT_PASSWORD_ROUTE_PATH,
  FACILITY_RESET_PASSWORD_ROUTE_PATH,
  RE_SUBMIT_OR_CANCEL_CONSULT_ROUTE_PATH,
  TWO_FACTOR_AUTHENTICATION_ROUTE_PATH,
} from '../../routers/RoutPath';
import { useAuth } from '../components/AuthContext';
import { ROLES } from '../../roles';
import { initAudio, warmUpAudio } from '../../shared/lib/utils/audioManager';
import AlertSound from '@assets/ringtone.mp3';

const SignInComponent = () => {
  const MessageRef = useRef('');
  const [messageType, setMessageType] = useState('');
  const { login } = useAuth();

  const { mutate: mutateFacilityLogin } = useFacilityLogin();
  const navigate = useNavigate();
  const location = useLocation();

  const [openToast, setOpenToast] = useState(false);

  const navigateForgotPassword = () => {
    navigate(FACILITY_FORGOT_PASSWORD_ROUTE_PATH, { replace: true });

    setOpenToast(false);
    setMessageType('');
  };

  const handleFacilityUserLogin = (data: FacilityLoginResponse) => {
    login(data);
    sessionStorage.setItem('errorFlag', 'false');
    if (data.isWaitingRoom) {
      // Unlock audio for iOS/iPadOS before navigating to Waiting screen
      initAudio(AlertSound);
      warmUpAudio();
      navigate(
        `${CONSULT_WAITING_SCREEN_ROUTE_PATH}?consultId=${data.consultId}`,
        {
          replace: true,
        }
      );
    } else if (data.isErrorPage) {
      navigate(`${RE_SUBMIT_OR_CANCEL_CONSULT_ROUTE_PATH}`, {
        replace: true,
        state: { consultId: data.consultId },
      });
    } else {
      navigate(FACILITY_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH, {
        replace: true,
      });
    }
  };

  const handleCaseManagerLogin = (data: FacilityLoginResponse) => {
    if (data.passwordChangeRequired) {
      sessionStorage.setItem('pendingMfaUser', JSON.stringify(data));
      if (data.accessToken) {
        localStorage.setItem('token', data.accessToken);
      }
      navigate(FACILITY_RESET_PASSWORD_ROUTE_PATH, {
        replace: true,
      });
    } else if (data.requiredMfa && !data.passwordChangeRequired) {
      sessionStorage.setItem('pendingMfaUser', JSON.stringify(data));
      if (data.accessToken) {
        localStorage.setItem('token', data.accessToken);
      }
      navigate(TWO_FACTOR_AUTHENTICATION_ROUTE_PATH, {
        replace: true,
      });
    } else {
      login(data);
      navigate(CASEMANGER_CONSULT_REQUEST_DASHBOARD_ROUTE_PATH, {
        replace: true,
      });
    }
  };

  const handleLogin = (request: FacilityLogin) => {
    mutateFacilityLogin(
      {
        username: request.username,
        password: request.password,
      },
      {
        onSuccess: (data: FacilityLoginResponse) => {
          if (data.role === ROLES.FACILITY_USER) {
            handleFacilityUserLogin(data);
          } else if (data.role === ROLES.CASE_MANAGER) {
            handleCaseManagerLogin(data);
          }
        },

        onError: (error: Error | FacilityResponce) => {
          MessageRef.current = error.message;
          setOpenToast(true);
          setMessageType('error');
          setTimeout(() => {
            setOpenToast(false);

            MessageRef.current = '';
          }, 10000);
        },
      }
    );
  };

  const [message, setMessage] = useState('');

  useEffect(() => {
    if (location?.state?.logout) {
      setMessage(
        'You have been logged out due to inactivity. Please log in again.'
      );
    } else {
      setMessage('');
    }
  }, [location.state]);

  const handleClose_SnackBar = () => {
    setOpenToast(false);
  };
  return (
    <Box className="container">
      {openToast && (
        <CustomSnackbar
          id="login-toast"
          successOrError={messageType as AlertColor}
          open={openToast}
          handleClose={handleClose_SnackBar}
          toastrMessage={MessageRef.current}
        />
      )}
      <Box className="center-div">
        <Box>
          {message && (
            <Box style={{ color: 'red', marginBottom: '12px' }}>{message}</Box>
          )}
        </Box>
        <Box className="logo-qler">
          <QLerLogo />
        </Box>
        <Box>
          <Login
            handleSubmitData={handleLogin}
            onForgotPassword={navigateForgotPassword}
          />
        </Box>
      </Box>
      <QlerFooter />
    </Box>
  );
};

export default SignInComponent;
